import React, { createRef, ReactNode } from 'react';
import { create } from 'zustand';
import {
  DialogContextProps,
  DialogFlag,
  DialogFlagResult,
  DialogId,
  DialogOptions,
  DialogProps,
  shakeX,
} from './helpers';

interface DialogStore {
  dialogs: DialogProps<any>[];
  show: <T>(
    options: DialogOptions<T>,
    children?: ReactNode
  ) => PromiseLike<DialogFlagResult<T>> & {
    id: DialogId;
  };
  contains: (id: DialogId) => boolean;
  retrieve: (id: DialogId) => DialogProps | void;
  update: (id: DialogId, props: Partial<DialogProps>) => DialogProps | void;
  requestClose: (id: DialogId, flag?: DialogFlag) => PromiseLike<void> | void;
  close: (
    id: DialogId,
    flag: DialogFlag,
    returnValue?: unknown
  ) => PromiseLike<void> | void;
}

export const useDialogStore = create<DialogStore>((set, get) => ({
  dialogs: [],
  show: <T>(options: DialogOptions<T>, children?: ReactNode) => {
    const resolver = Promise.withResolvers<DialogFlagResult<T>>();
    const dialog: DialogProps<T> = {
      id: crypto.randomUUID(),
      children,
      resolver,
      loadingFlag: DialogFlag.NONE,
      forwardedRef: createRef(),
      elementRef: createRef(),
      ...options,
    };
    set(({ dialogs }) => ({ dialogs: [...dialogs, dialog] }));
    return Object.assign(resolver.promise, {
      id: dialog.id,
    });
  },
  contains: (id: DialogId) => {
    const { dialogs } = get();
    return dialogs.some(dialog => dialog.id === id);
  },
  retrieve: (id: DialogId) => {
    const { dialogs } = get();
    return dialogs.find(dialog => dialog.id === id);
  },
  update: (id: DialogId, props: Partial<DialogProps>) => {
    set(({ dialogs }) => ({
      dialogs: dialogs.map(dialog =>
        dialog.id === id ? { ...dialog, ...props } : dialog
      ),
    }));
  },
  requestClose: async (id: DialogId, flag: DialogFlag = DialogFlag.CLOSE) => {
    if (get().contains(id)) {
      get().update(id, { loadingFlag: flag });
      const dialog = get().retrieve(id)!;
      const returnValue = await dialog.forwardedRef.current?.(flag);
      const pFlag = await dialog.onRequestClose?.(flag, returnValue);
      // 如果返回了新的 pFlag 那就使用新的
      if (typeof pFlag === 'number') {
        flag = pFlag;
      }
      get().update(id, { loadingFlag: DialogFlag.NONE });
      if (flag & 0xffffff) {
        if (flag & DialogFlag.REJECT) {
          // 拒绝后播放 shakeX 动画
          const element = dialog.elementRef.current;
          if (element != null) {
            await shakeX(element).finished;
          }
        } else {
          get().close(id, flag, returnValue);
        }
      }
    }
  },
  close: async (id: DialogId, flag: DialogFlag, returnValue?: unknown) => {
    const dialog = get().retrieve(id);
    if (dialog != null) {
      dialog.resolver.resolve(Object.assign(flag, { returnValue }));
      const dismissedResolver = Promise.withResolvers<void>();
      // 更新 dialogs 中的 dismissedResolver
      set(({ dialogs }) => ({
        dialogs: dialogs.map(dialog =>
          dialog.id === id ? { ...dialog, dismissedResolver } : dialog
        ),
      }));
      // 设置 visible 为 false
      await dismissedResolver.promise;
      // 从 dialogs 中移除 dialog
      set(({ dialogs }) => ({
        dialogs: dialogs.filter(dialog => dialog.id != id),
      }));
    }
  },
}));

export const DialogContext = React.createContext<DialogContextProps>(
  null as any as DialogContextProps
);
